import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Dumbbell, CalendarDays, Users,
  LogOut, Menu, Activity, Building, ClipboardList, Search, Bookmark,
  ChevronsLeft, ChevronsRight, Radio, Coins, Mail, UserPlus, Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import ThemeSwitcher from '@/components/ThemeSwitcher';

const navGroups = [
  {
    label: 'Overview',
    items: [
      { href: '/live-studio', icon: Radio, label: 'Live Studio' },
    ],
  },
  {
    label: 'Content',
    items: [
      { href: '/exercises', icon: Dumbbell, label: 'Exercises' },
      { href: '/exercises/search', icon: Search, label: 'Advanced Search' },
      { href: '/bookmarks', icon: Bookmark, label: 'Bookmarks' },
      { href: '/sessions', icon: CalendarDays, label: 'Sessions' },
      { href: '/session-builder', icon: ClipboardList, label: 'Session Builder' },
    ],
  },
  {
    label: 'Users',
    items: [
      { href: '/new-subscribers', icon: UserPlus, label: 'New Subscribers' },
      { href: '/user-program', icon: Users, label: 'User Programs' },
      { href: '/user-activity', icon: Activity, label: 'User Activity' },
    ],
  },
  {
    label: 'System',
    items: [
      { href: '/email-studio', icon: Mail, label: 'Email Studio' },
      { href: '/token-usage', icon: Coins, label: 'Token Usage' },
      { href: '/b2b', icon: Building, label: 'B2B' },
    ],
  },
];

const NavItem = ({ href, icon: Icon, label, onClick, isCollapsed }) => {
  const linkContent = (
    <NavLink
      to={href}
      end={href === '/'}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-accent text-accent-foreground'
            : 'text-muted-foreground hover:bg-accent/50 hover:text-accent-foreground',
          isCollapsed && 'justify-center px-2'
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-[2px] rounded-r bg-foreground" />
          )}
          <Icon className="h-4 w-4 shrink-0" />
          {!isCollapsed && <span>{label}</span>}
        </>
      )}
    </NavLink>
  );

  if (isCollapsed) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
          <TooltipContent side="right" className="text-xs">
            {label}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return linkContent;
};

const SidebarContent = ({ onLinkClick, isCollapsed, toggleSidebar, user, signOut, groups }) => (
  <div className="flex h-full flex-col">
    {/* Logo / Brand */}
    <div className={cn(
      'flex h-14 items-center border-b px-3',
      isCollapsed ? 'justify-center' : 'gap-2'
    )}>
      {!isCollapsed && (
        <>
          <img
            src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/logo/AppIcon.png`}
            alt="Logo"
            className="h-6 w-6 rounded"
          />
          <span className="text-sm font-semibold truncate">My Movement Space</span>
        </>
      )}
      <Button
        onClick={toggleSidebar}
        variant="ghost"
        size="icon"
        className={cn(
          'h-7 w-7 text-muted-foreground hover:text-foreground shrink-0',
          !isCollapsed && 'ml-auto'
        )}
      >
        {isCollapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
      </Button>
    </div>

    {/* Navigation */}
    <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
      {groups.map((group) => (
        <div key={group.label}>
          {!isCollapsed && (
            <p className="mb-1 px-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/60">
              {group.label}
            </p>
          )}
          {isCollapsed && group.label !== 'Overview' && (
            <Separator className="my-2" />
          )}
          <div className="space-y-0.5">
            {group.items.map((item) => (
              <NavItem
                key={item.href}
                {...item}
                onClick={onLinkClick}
                isCollapsed={isCollapsed}
              />
            ))}
          </div>
        </div>
      ))}
    </nav>

    {/* Bottom section */}
    <div className="border-t px-2 py-3 space-y-2">
      <ThemeSwitcher isCollapsed={isCollapsed} />
      {!isCollapsed ? (
        <div className="flex items-center gap-2 rounded-md px-2 py-1.5">
          <img
            className="h-6 w-6 rounded-full"
            alt={user?.email}
            src={user?.user_metadata?.avatar_url || `https://ui-avatars.com/api/?name=${user?.email}&background=171717&color=fafafa&size=32`}
          />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium truncate">{user?.email}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground shrink-0"
            onClick={signOut}
          >
            <LogOut className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="w-full h-8 text-muted-foreground hover:text-foreground"
                onClick={signOut}
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs">Sign out</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  </div>
);

const MainLayout = ({ children }) => {
  const { user, signOut, isSupport, canAccessRoute } = useAuth();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const location = useLocation();

  const visibleNavGroups = navGroups
    .map((group) => ({ ...group, items: group.items.filter((item) => canAccessRoute(item.href)) }))
    .filter((group) => group.items.length > 0);

  const getInitialSidebarState = () => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('sidebarCollapsed') === 'true';
  };

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(getInitialSidebarState);

  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('sidebarCollapsed', String(next));
      return next;
    });
  };

  const getBreadcrumbs = () => {
    const path = location.pathname;
    const crumbs = [{ label: 'Home', href: '/' }];

    for (const group of visibleNavGroups) {
      for (const item of group.items) {
        if (item.href !== '/' && path.startsWith(item.href)) {
          crumbs.push({ label: item.label, href: item.href });
        }
      }
    }

    if (path.startsWith('/exercise/') && !path.includes('search')) {
      crumbs.push({ label: 'Exercises', href: '/exercises' });
      crumbs.push({ label: 'Detail', href: path });
    }
    if (path.startsWith('/program/')) {
      crumbs.push({ label: 'Programs', href: '/programs' });
      crumbs.push({ label: 'Detail', href: path });
    }
    if (path.startsWith('/exercises/search')) {
      crumbs.push({ label: 'Exercises', href: '/exercises' });
      crumbs.push({ label: 'Advanced Search', href: path });
    }

    return crumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <div className="flex min-h-screen w-full bg-background">
      {/* Desktop sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-10 hidden flex-col border-r bg-background sm:flex transition-all duration-200',
          isSidebarCollapsed ? 'w-[52px]' : 'w-[240px]'
        )}
      >
        <SidebarContent
          isCollapsed={isSidebarCollapsed}
          toggleSidebar={toggleSidebar}
          user={user}
          signOut={signOut}
          groups={visibleNavGroups}
        />
      </aside>

      {/* Main content area */}
      <div
        className={cn(
          'flex flex-col w-full transition-all duration-200',
          isSidebarCollapsed ? 'sm:pl-[52px]' : 'sm:pl-[240px]'
        )}
      >
        {/* Header */}
        <header className="sticky top-0 z-30 flex h-14 items-center gap-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 sm:px-6">
          {/* Mobile menu */}
          <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
            <SheetTrigger asChild>
              <Button size="icon" variant="ghost" className="sm:hidden h-8 w-8">
                <Menu className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[240px] p-0">
              <SidebarContent
                onLinkClick={() => setIsSheetOpen(false)}
                isCollapsed={false}
                toggleSidebar={toggleSidebar}
                user={user}
                signOut={signOut}
                groups={visibleNavGroups}
              />
            </SheetContent>
          </Sheet>

          {/* Breadcrumbs */}
          <nav className="flex items-center gap-1.5 text-sm">
            {breadcrumbs.map((crumb, i) => (
              <React.Fragment key={crumb.href + i}>
                {i > 0 && <span className="text-muted-foreground/40">/</span>}
                {i === breadcrumbs.length - 1 ? (
                  <span className="font-medium text-foreground">{crumb.label}</span>
                ) : (
                  <NavLink
                    to={crumb.href}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {crumb.label}
                  </NavLink>
                )}
              </React.Fragment>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            {/* Cmd+K hint */}
            <button
              className="hidden sm:flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
              onClick={() => {
                const event = new KeyboardEvent('keydown', { key: 'k', metaKey: true });
                document.dispatchEvent(event);
              }}
            >
              <Search className="h-3 w-3" />
              <span>Search...</span>
              <kbd className="pointer-events-none ml-2 inline-flex h-5 select-none items-center gap-1 rounded border bg-background px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                <span className="text-xs">⌘</span>K
              </kbd>
            </button>
          </div>
        </header>

        {isSupport && (
          <div className="flex items-center gap-2 border-b bg-muted/50 px-4 sm:px-6 py-2 text-xs text-muted-foreground">
            <Info className="h-3.5 w-3.5 shrink-0" />
            <span>You see the users assigned to you.</span>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 p-4 sm:p-6 space-y-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
